import type { IndividualPaymentHeaderViewModel } from '@/shared/api/generated';
import type { LookupOption } from '@/shared/ui/molecules/LookupAutocomplete';

import { useIndividualPaymentHeader } from './useIndividualPaymentHeader';
import { usePaymentTypeLabel } from './usePaymentTypeLabel';
import { useAvailablePaymentMethods } from './usePlanLookups';

/** Dados de apoio da tela de criação de parcela. */
export interface ParcelCreateLookups {
  /** Cabeçalho do contrato — alimenta o card somente leitura. */
  readonly header: IndividualPaymentHeaderViewModel | undefined;
  readonly isHeaderLoading: boolean;
  readonly isHeaderError: boolean;
  readonly refetchHeader: () => void;
  /** Formas de pagamento aceitas por **este** contrato. */
  readonly paymentMethods: readonly LookupOption[];
  readonly arePaymentMethodsLoading: boolean;
  /**
   * Contrato consultado, resposta recebida e nenhuma forma de pagamento.
   *
   * Distinto de "ainda carregando": aqui o backend respondeu que o plano não tem
   * método disponível. Sem dizer isso, o operador ficaria diante de um campo
   * obrigatório que não abre, sem saber se é a tela ou o cadastro do contrato.
   */
  readonly hasNoPaymentMethod: boolean;
  /** Rótulo da forma de pagamento do plano — o header devolve só o id. */
  readonly planPaymentTypeLabel: string;
}

/**
 * Reúne o que a tela de criação de parcela precisa ler antes de escrever.
 *
 * As três consultas são independentes de propósito: falha nas formas de pagamento
 * não impede a leitura do contrato, e vice-versa. O `usePaymentTypeLabel` resolve o
 * `paymentType` numérico do header pelo lookup global — o mesmo dicionário que a
 * tela de parcelas usa.
 *
 * @param financialPersonId - ID do contrato; `null` desabilita todas as queries.
 * @returns Cabeçalho, formas de pagamento do contrato e estados de carga.
 *
 * @example
 * ```tsx
 * const { header, paymentMethods, hasNoPaymentMethod } = useParcelCreateLookups(101);
 * ```
 */
export function useParcelCreateLookups(financialPersonId: number | null): ParcelCreateLookups {
  const { header, isLoading, isError, refetch } = useIndividualPaymentHeader(financialPersonId);
  const { paymentMethods, isLoading: arePaymentMethodsLoading } =
    useAvailablePaymentMethods(financialPersonId);
  const { label: planPaymentTypeLabel } = usePaymentTypeLabel(
    header?.paymentType,
    financialPersonId !== null,
  );

  return {
    header,
    isHeaderLoading: isLoading,
    isHeaderError: isError,
    refetchHeader: () => void refetch(),
    paymentMethods,
    arePaymentMethodsLoading,
    hasNoPaymentMethod:
      financialPersonId !== null && !arePaymentMethodsLoading && paymentMethods.length === 0,
    planPaymentTypeLabel,
  } as const;
}
